import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent
} from '@angular/common/http';
import { Router } from '@angular/router';
import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthService, isRefreshTokenUrl } from '../../service/auth.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(
    private router: Router,
    private authService: AuthService
  ) { }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (!window.location.href.includes('/login'))
      sessionStorage.setItem("comeBackTo", window.location.href);

    // The refresh call itself is owned by the auth layer; it must not recurse through here.
    if (isRefreshTokenUrl(req.url)) {
      return next.handle(req);
    }

    if (!req.headers.has('authorization')) {
      return next.handle(req);
    }

    if (this.authService.doesRefreshTokenExpired()) {
      this.authService.clearSession();
      this.router.navigateByUrl('/login');
      return EMPTY;
    }

    if (this.authService.doesTokenExpired()) {
      // A failed refresh already cleared the session and routed to login; letting the error
      // through terminates this request instead of leaving it queued forever.
      return this.authService.refresh().pipe(
        switchMap(() => this.sendAndRecover(req, next))
      );
    }

    return this.sendAndRecover(req, next);
  }

  /**
   * Sends the request and recovers from a single 401 with one refresh and one retry.
   *
   * The pre-emptive buffer reads the client clock, so a token can still be rejected here when the
   * clock is skewed or the session was ended server-side. A second 401 means the new token is not
   * being accepted either, so the session is over.
   */
  private sendAndRecover(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return next.handle(this.withCurrentToken(req)).pipe(
      catchError((err) => {
        if (!this.isUnauthorized(err)) {
          return throwError(err);
        }
        return this.authService.refresh().pipe(
          switchMap(() =>
            next.handle(this.withCurrentToken(req)).pipe(
              catchError((retryErr) => {
                if (this.isUnauthorized(retryErr)) {
                  this.authService.clearSession();
                  this.router.navigateByUrl('/login');
                }
                return throwError(retryErr);
              })
            )
          )
        );
      })
    );
  }

  private isUnauthorized(err: any): boolean {
    return err instanceof HttpErrorResponse && err.status === 401;
  }

  private withCurrentToken(req: HttpRequest<any>): HttpRequest<any> {
    return req.clone({
      setHeaders: { Authorization: `Bearer ${this.authService.token}` }
    });
  }
}
